import React, { useEffect, useState, useCallback } from "react";
import client, { apiErr } from "../lib/api";
import { LANGS } from "../i18n";
import { Card, Button, Input, Select, Badge, Modal } from "../components/ui";
import { Network, Building2, Plus, Trash2, Pencil, Save, X, Search, Globe, Tag, Users, ShieldCheck, IdCard } from "lucide-react";

const langLabel = (code) => (LANGS.find((l) => l.code === code)?.label || code);

export default function TeamDirectory() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [q, setQ] = useState("");
  const [fRole, setFRole] = useState("");
  const [fOffice, setFOffice] = useState("");
  const [fLang, setFLang] = useState("");
  const [fRoleTag, setFRoleTag] = useState("");
  const [fDelegable, setFDelegable] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [officeModal, setOfficeModal] = useState(false);
  const [roleModal, setRoleModal] = useState(false);
  const [newOffice, setNewOffice] = useState("");
  const [newRole, setNewRole] = useState("");
  const [editMeta, setEditMeta] = useState(null); // member being edited

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (fRole) p.set("role", fRole);
    if (fOffice) p.set("office", fOffice);
    if (fLang) p.set("language", fLang);
    if (fRoleTag) p.set("role_tag", fRoleTag);
    if (fDelegable) p.set("delegable", fDelegable);
    if (sortBy) p.set("sort", sortBy);
    if (q) p.set("q", q);
    client.get(`/admin/team?${p.toString()}`).then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));
  }, [fRole, fOffice, fLang, fRoleTag, fDelegable, sortBy, q]);

  useEffect(() => { load(); }, [load]);

  const offices = data?.offices || [];
  const roles = data?.roles || [];
  const maxLangs = data?.max_langs || 5;
  const levels = data?.delegable_levels || 9;
  const officeName = (id) => offices.find((o) => o.id === id)?.name || "—";
  const roleName = (id) => roles.find((r) => r.id === id)?.name || "";

  const addOffice = async () => {
    if (!newOffice.trim()) return;
    try { await client.post("/admin/offices", { name: newOffice.trim() }); setNewOffice(""); load(); }
    catch (e) { setErr(apiErr(e)); }
  };
  const removeOffice = async (id) => {
    if (!window.confirm("Delete this office? It will be removed from all team members.")) return;
    try { await client.delete(`/admin/offices/${id}`); load(); } catch (e) { setErr(apiErr(e)); }
  };
  const addRole = async () => {
    if (!newRole.trim()) return;
    try { await client.post("/admin/roles", { name: newRole.trim() }); setNewRole(""); load(); }
    catch (e) { setErr(apiErr(e)); }
  };
  const removeRole = async (id) => {
    if (!window.confirm("Delete this role? It will be removed from all team members.")) return;
    try { await client.delete(`/admin/roles/${id}`); load(); } catch (e) { setErr(apiErr(e)); }
  };

  const saveMeta = async () => {
    try {
      await client.put(`/admin/team/${editMeta.id}/meta`, {
        offices: editMeta.offices, languages: editMeta.languages, categories: editMeta.categories,
        role_tag: editMeta.role_tag || null, delegable: !!editMeta.delegable,
        delegable_level: editMeta.delegable ? (editMeta.delegable_level || levels) : null,
      });
      await client.put(`/admin/team/${editMeta.id}/manager`, {
        level: editMeta.manager_level || "none", offices: editMeta.offices,
      });
      setMsg(`Updated ${editMeta.name}.`); setEditMeta(null); load();
    } catch (e) { setErr(apiErr(e)); }
  };

  const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  const toggleLang = (c) => {
    const on = editMeta.languages.includes(c);
    if (!on && editMeta.languages.length >= maxLangs) { setErr(`A staffer can hold at most ${maxLangs} languages.`); return; }
    setErr(""); setEditMeta({ ...editMeta, languages: toggle(editMeta.languages, c) });
  };

  return (
    <div className="space-y-6 fadeup" data-testid="team-page">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-head text-2xl sm:text-3xl flex items-center gap-2"><Network size={24} /> Offices &amp; Team</h1>
        <span className="opacity-60 text-sm">Group admin staff by office, role, language and authority — the basis for delegating work at scale. Runners keep their own role.</span>
        <div className="ms-auto flex gap-2">
          <Button variant="subtle" onClick={() => setRoleModal(true)} data-testid="manage-roles"><IdCard size={16} /> Manage roles</Button>
          <Button variant="subtle" onClick={() => setOfficeModal(true)} data-testid="manage-offices"><Building2 size={16} /> Manage offices</Button>
        </div>
      </div>

      {err && <Card className="border-[#DA1E28]/40 text-sm text-[#DA1E28]" data-testid="team-err">{err}</Card>}
      {msg && <Card className="border-[var(--primary)]/40 text-sm" data-testid="team-msg">{msg}</Card>}

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative max-w-xs flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 opacity-50" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email..."
            className="w-full rounded-md border border-black/15 dark:border-white/15 bg-white/60 dark:bg-black/30 py-2 ps-9 pe-3 text-sm" data-testid="team-search" />
        </div>
        <div className="w-36"><Select label="Type" value={fRole} onChange={(e) => setFRole(e.target.value)} data-testid="team-filter-role">
          <option value="">All types</option><option value="support">Admin staff</option><option value="runner">Runner</option>
        </Select></div>
        <div className="w-40"><Select label="Role" value={fRoleTag} onChange={(e) => setFRoleTag(e.target.value)} data-testid="team-filter-roletag">
          <option value="">All roles</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </Select></div>
        <div className="w-40"><Select label="Office" value={fOffice} onChange={(e) => setFOffice(e.target.value)} data-testid="team-filter-office">
          <option value="">All offices</option>
          {offices.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </Select></div>
        <div className="w-40"><Select label="Language" value={fLang} onChange={(e) => setFLang(e.target.value)} data-testid="team-filter-lang">
          <option value="">All languages</option>
          {(data?.languages || []).map((c) => <option key={c} value={c}>{langLabel(c)}</option>)}
        </Select></div>
        <div className="w-36"><Select label="Delegable" value={fDelegable} onChange={(e) => setFDelegable(e.target.value)} data-testid="team-filter-delegable">
          <option value="">Any</option><option value="yes">Delegable</option><option value="no">Not delegable</option>
        </Select></div>
        <div className="w-40"><Select label="Sort" value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="team-sort">
          <option value="">Default</option><option value="delegable">Seniority (delegable)</option><option value="name">Name</option>
        </Select></div>
        <Badge color="info" data-testid="team-count"><Users size={12} className="inline" /> {data?.members?.length || 0} shown</Badge>
      </div>

      <div className="grid gap-3">
        {(data?.members || []).length === 0 && <Card className="text-center opacity-60">No team members match these filters.</Card>}
        {(data?.members || []).map((m) => (
          <Card key={m.id} className="space-y-2" data-testid={`team-${m.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-head font-medium">{m.name}</span>
              <Badge color={m.role === "support" ? "info" : "success"}>{m.role === "support" ? "Admin staff" : "Runner"}</Badge>
              {m.role_tag && <Badge color="neutral" data-testid={`team-role-${m.id}`}><IdCard size={11} className="inline" /> {roleName(m.role_tag)}</Badge>}
              {m.delegable && <Badge color="warning" data-testid={`team-delegable-${m.id}`}><ShieldCheck size={11} className="inline" /> Delegable · L{m.delegable_level}</Badge>}
              {m.manager_level && <Badge color="warning">{m.manager_level === "high_level" ? "High-level mgr" : "Runner mgr"}</Badge>}
              <span className="text-sm opacity-60">{m.email}</span>
              {m.disabled && <Badge color="danger">disabled</Badge>}
              <Button className="ms-auto" variant="subtle" onClick={() => setEditMeta({ ...m })} data-testid={`edit-meta-${m.id}`}><Pencil size={14} /> Edit</Button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {m.offices.length ? m.offices.map((o) => <span key={o} className="flex items-center gap-1 rounded-full bg-black/10 px-2 py-0.5 dark:bg-white/10"><Building2 size={11} />{officeName(o)}</span>) : <span className="opacity-40">no office</span>}
              {m.languages.map((l) => <span key={l} className="flex items-center gap-1 rounded-full bg-[var(--primary)]/10 px-2 py-0.5 text-[var(--primary)]"><Globe size={11} />{langLabel(l)}</span>)}
              {m.categories.map((c) => <span key={c} className="flex items-center gap-1 rounded-full border border-black/15 px-2 py-0.5 dark:border-white/15"><Tag size={11} />{c}</span>)}
            </div>
          </Card>
        ))}
      </div>

      {/* Offices manager */}
      <Modal open={officeModal} onClose={() => setOfficeModal(false)} title="Offices" testid="offices-modal">
        <div className="space-y-3 text-sm">
          <p className="opacity-60">Define up to {data?.max_offices || 7} offices. Team members can belong to more than one.</p>
          <div className="space-y-2">
            {offices.map((o) => (
              <div key={o.id} className="flex items-center gap-2 rounded-md border border-black/10 px-3 py-2 dark:border-white/10" data-testid={`office-${o.id}`}>
                <Building2 size={15} className="opacity-60" />
                <span className="font-medium">{o.name}</span>
                <button onClick={() => removeOffice(o.id)} data-testid={`del-office-${o.id}`} className="ms-auto text-[#DA1E28] hover:opacity-70"><Trash2 size={15} /></button>
              </div>
            ))}
            {offices.length === 0 && <p className="opacity-50">No offices yet.</p>}
          </div>
          {offices.length < (data?.max_offices || 7) && (
            <div className="flex gap-2">
              <Input placeholder="New office name (e.g. Lagos HQ)" value={newOffice} onChange={(e) => setNewOffice(e.target.value)} data-testid="new-office-name" />
              <Button onClick={addOffice} data-testid="add-office"><Plus size={15} /> Add</Button>
            </div>
          )}
        </div>
      </Modal>

      {/* Roles manager */}
      <Modal open={roleModal} onClose={() => setRoleModal(false)} title="Admin roles" testid="roles-modal">
        <div className="space-y-3 text-sm">
          <p className="opacity-60">Define up to {data?.max_roles || 20} admin-staff roles (e.g. Supervisor, Agent, Biller). These are descriptive authority tags — actual panel access is still granted manually in delegation.</p>
          <div className="space-y-2">
            {roles.map((r) => (
              <div key={r.id} className="flex items-center gap-2 rounded-md border border-black/10 px-3 py-2 dark:border-white/10" data-testid={`role-${r.id}`}>
                <IdCard size={15} className="opacity-60" />
                <span className="font-medium">{r.name}</span>
                <button onClick={() => removeRole(r.id)} data-testid={`del-role-${r.id}`} className="ms-auto text-[#DA1E28] hover:opacity-70"><Trash2 size={15} /></button>
              </div>
            ))}
            {roles.length === 0 && <p className="opacity-50">No roles yet.</p>}
          </div>
          {roles.length < (data?.max_roles || 20) && (
            <div className="flex gap-2">
              <Input placeholder="New role name (e.g. Supervisor)" value={newRole} onChange={(e) => setNewRole(e.target.value)} data-testid="new-role-name" />
              <Button onClick={addRole} data-testid="add-role"><Plus size={15} /> Add</Button>
            </div>
          )}
        </div>
      </Modal>

      {/* Per-member metadata editor */}
      <Modal open={!!editMeta} onClose={() => setEditMeta(null)} title={editMeta ? `Edit ${editMeta.name}` : ""} testid="meta-modal" wide>
        {editMeta && (
          <div className="space-y-4 text-sm">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">Offices</div>
              <div className="flex flex-wrap gap-1.5">
                {offices.length === 0 && <span className="opacity-50">Add offices first.</span>}
                {offices.map((o) => {
                  const on = editMeta.offices.includes(o.id);
                  return <button key={o.id} data-testid={`meta-office-${o.id}`} onClick={() => setEditMeta({ ...editMeta, offices: toggle(editMeta.offices, o.id) })}
                    className={`rounded-full border px-2.5 py-1 ${on ? "border-[var(--primary)] bg-[var(--primary)] text-white" : "border-black/15 dark:border-white/15"}`}>{o.name}</button>;
                })}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">Languages <span className="opacity-60">({editMeta.languages.length}/{maxLangs})</span></div>
              <div className="flex flex-wrap gap-1.5">
                {(data?.languages || []).map((c) => {
                  const on = editMeta.languages.includes(c);
                  return <button key={c} data-testid={`meta-lang-${c}`} onClick={() => toggleLang(c)}
                    className={`rounded-full border px-2.5 py-1 ${on ? "border-[var(--primary)] bg-[var(--primary)] text-white" : "border-black/15 dark:border-white/15"}`}>{langLabel(c)}</button>;
                })}
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">Role</div>
                <Select value={editMeta.role_tag || ""} onChange={(e) => setEditMeta({ ...editMeta, role_tag: e.target.value })} data-testid="meta-role-tag">
                  <option value="">— no role —</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
                {roles.length === 0 && <p className="mt-1 text-xs opacity-55">Define roles via “Manage roles”.</p>}
              </div>
              <div className="rounded-md border border-amber-500/30 bg-amber-500/5 p-2.5">
                <label className="flex items-center gap-2 font-medium" data-testid="meta-delegable-wrap">
                  <input type="checkbox" checked={!!editMeta.delegable}
                    onChange={(e) => setEditMeta({ ...editMeta, delegable: e.target.checked, delegable_level: e.target.checked ? (editMeta.delegable_level || levels) : null })}
                    data-testid="meta-delegable" />
                  <ShieldCheck size={15} className="text-amber-600 dark:text-amber-400" /> Delegable
                </label>
                {editMeta.delegable && (
                  <div className="mt-2">
                    <div className="mb-1 text-xs opacity-60">Authority level (1 = most senior, {levels} = lowest)</div>
                    <Select value={editMeta.delegable_level || levels} onChange={(e) => setEditMeta({ ...editMeta, delegable_level: Number(e.target.value) })} data-testid="meta-delegable-level">
                      {Array.from({ length: levels }, (_, i) => i + 1).map((n) => (
                        <option key={n} value={n}>Level {n}{n === 1 ? " (most senior)" : n === levels ? " (lowest)" : ""}</option>
                      ))}
                    </Select>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">Category tags</div>
              <Input placeholder="Comma-separated (e.g. tier2, billing, VIP)" value={(editMeta.categories || []).join(", ")}
                onChange={(e) => setEditMeta({ ...editMeta, categories: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })} data-testid="meta-categories" />
            </div>
            <div className="rounded-md border border-[var(--primary)]/30 bg-[var(--primary)]/5 p-2.5">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide opacity-60">Manager role (cascade)</div>
              <Select value={editMeta.manager_level || "none"} onChange={(e) => setEditMeta({ ...editMeta, manager_level: e.target.value })} data-testid="meta-manager-level">
                <option value="none">Not a manager</option>
                <option value="high_level">High-level manager (≤35)</option>
                <option value="runner_mgmt">Runner-management manager (≤35)</option>
              </Select>
              <p className="mt-1 text-xs opacity-55">Managers oversee only their office(s) above, and assign their own agents to specific runners.</p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEditMeta(null)} data-testid="meta-cancel"><X size={15} /> Cancel</Button>
              <Button onClick={saveMeta} data-testid="meta-save"><Save size={15} /> Save</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
